/**
 * Copyright 2025 Beijing Volcano Engine Technology Co., Ltd. All Rights Reserved.
 * SPDX-license-identifier: BSD-3-Clause
 */

interface RequestOptions {
  headers?: Record<string, string>;
  params?: Record<string, any>;
  body?: any;
  timeout?: number;
}

interface RequestConfig extends RequestInit {
  headers: Headers;
}

/**
 * HTTP 请求工具类
 */
class Request {
  private baseURL: string;

  private defaultHeaders: Record<string, string>;

  private timeout: number;

  constructor(baseURL = '', timeout = 10000) {
    this.baseURL = baseURL;
    this.timeout = timeout;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
    };
  }

  /**
   * 设置基础 URL
   */
  setBaseURL(url: string) {
    this.baseURL = url;
  }

  /**
   * 设置默认请求头
   */
  setDefaultHeaders(headers: Record<string, string>) {
    this.defaultHeaders = { ...this.defaultHeaders, ...headers };
  }

  /**
   * 构建完整 URL（带查询参数）
   */
  private buildURL(url: string, params?: Record<string, any>): string {
    const fullURL = url.startsWith('http') ? url : `${this.baseURL}${url}`;
    
    if (!params || Object.keys(params).length === 0) {
      return fullURL;
    }

    const queryString = Object.keys(params)
      .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
      .join('&');

    return `${fullURL}${fullURL.includes('?') ? '&' : '?'}${queryString}`;
  }

  /**
   * 处理请求超时
   */
  private createTimeoutPromise(timeout: number): Promise<never> {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeout}ms`));
      }, timeout);
    });
  }

  /**
   * 通用请求方法
   */
  private async request<T>(
    url: string,
    method: string,
    options: RequestOptions = {}
  ): Promise<T> {
    const { headers = {}, params, body, timeout = this.timeout } = options;

    // 构建完整 URL
    const fullURL = this.buildURL(url, params);

    // 合并请求头
    const requestHeaders = new Headers({
      ...this.defaultHeaders,
      ...headers,
    });

    // 构建请求配置
    const config: RequestConfig = {
      method,
      headers: requestHeaders,
    };

    // 处理请求体
    if (body) {
      if (requestHeaders.get('Content-Type')?.includes('application/json')) {
        config.body = JSON.stringify(body);
      } else if (body instanceof FormData) {
        config.body = body;
        // FormData 会自动设置 Content-Type，需要删除默认的
        requestHeaders.delete('Content-Type');
      } else {
        config.body = body;
      }
    }

    // 将 Headers 对象转换为普通对象便于打印
    const headersObject: Record<string, string> = {};
    requestHeaders.forEach((value, key) => {
      headersObject[key] = value;
    });

    // 📤 打印请求信息
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #00d4ff');
    console.log(`%c📤 [请求] ${method} ${fullURL}`, 'color: #00d4ff; font-weight: bold; font-size: 13px');
    console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #00d4ff');
    
    if (params && Object.keys(params).length > 0) {
      console.log('%c📋 [Query Params]:', 'color: #52c41a; font-weight: bold');
      console.log(JSON.stringify(params, null, 2));
    }
    
    console.log('%c📋 [Headers]:', 'color: #faad14; font-weight: bold');
    console.log(JSON.stringify(headersObject, null, 2));
    
    if (body) {
      console.log('%c📋 [Body]:', 'color: #1890ff; font-weight: bold');
      if (body instanceof FormData) {
        console.log('[FormData]');
        // 尝试打印 FormData 内容
        const formDataEntries: Record<string, any> = {};
        body.forEach((value, key) => {
          formDataEntries[key] = value;
        });
        console.log(formDataEntries);
      } else if (typeof body === 'string') {
        try {
          console.log(JSON.stringify(JSON.parse(body), null, 2));
        } catch {
          console.log(body);
        }
      } else {
        console.log(JSON.stringify(body, null, 2));
      }
    }

    const requestStartTime = Date.now();

    try {
      // 发起请求（带超时控制）
      const response = await Promise.race([
        fetch(fullURL, config),
        this.createTimeoutPromise(timeout),
      ]);

      const requestDuration = Date.now() - requestStartTime;

      // 检查响应状态
      if (!response.ok) {
        // 📥 打印错误响应
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
        console.log(`%c❌ [响应错误] ${response.status} ${response.statusText}`, 'color: #ff4d4f; font-weight: bold; font-size: 13px');
        console.log(`%c⏱️ 耗时: ${requestDuration}ms`, 'color: #ff4d4f');
        console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
        
        throw new Error(`HTTP Error: ${response.status} ${response.statusText}`);
      }

      // 解析响应
      const contentType = response.headers.get('Content-Type');
      let responseData: any;
      
      if (contentType?.includes('application/json')) {
        responseData = await response.json();
      } else if (contentType?.includes('text')) {
        responseData = await response.text();
      } else {
        responseData = await response.blob();
      }

      // 📥 打印成功响应
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
      console.log(`%c✅ [响应成功] ${response.status} ${response.statusText}`, 'color: #52c41a; font-weight: bold; font-size: 13px');
      console.log(`%c⏱️ 耗时: ${requestDuration}ms`, 'color: #52c41a');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
      
      console.log('%c📋 [Response Data]:', 'color: #722ed1; font-weight: bold');
      if (responseData instanceof Blob) {
        console.log(`[Blob] size: ${responseData.size} bytes, type: ${responseData.type}`);
      } else if (typeof responseData === 'string') {
        try {
          console.log(JSON.stringify(JSON.parse(responseData), null, 2));
        } catch {
          console.log(responseData);
        }
      } else {
        console.log(JSON.stringify(responseData, null, 2));
      }
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #52c41a');
      console.log(''); // 空行分隔
      
      return responseData;
    } catch (error) {
      const requestDuration = Date.now() - requestStartTime;
      
      // 📥 打印请求失败
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
      console.log(`%c❌ [请求失败] ${method} ${fullURL}`, 'color: #ff4d4f; font-weight: bold; font-size: 13px');
      console.log(`%c⏱️ 耗时: ${requestDuration}ms`, 'color: #ff4d4f');
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
      console.error(error);
      console.log('%c━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━', 'color: #ff4d4f');
      console.log(''); // 空行分隔
      
      throw error;
    }
  }

  /**
   * GET 请求
   * @param url 请求地址
   * @param options 请求选项（params 会作为查询参数，headers 自定义请求头）
   */
  get<T = any>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, 'GET', options);
  }

  /**
   * POST 请求
   * @param url 请求地址
   * @param options 请求选项（body 作为请求体，params 作为查询参数，headers 自定义请求头）
   */
  post<T = any>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, 'POST', options);
  }

  /**
   * PUT 请求
   * @param url 请求地址
   * @param options 请求选项
   */
  put<T = any>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, 'PUT', options);
  }

  /**
   * DELETE 请求
   * @param url 请求地址
   * @param options 请求选项
   */
  delete<T = any>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, 'DELETE', options);
  }

  /**
   * PATCH 请求
   * @param url 请求地址
   * @param options 请求选项
   */
  patch<T = any>(url: string, options?: RequestOptions): Promise<T> {
    return this.request<T>(url, 'PATCH', options);
  }
}

// 创建默认实例
const request = new Request();

// 导出实例和类
export { Request };
export default request;
